import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Download, X, FileText, Camera, Upload, Mail } from "lucide-react";
import { RefBlock, CompanyOverrideBlock } from "@/pages/admin/InvoiceDocsPanel";
import { IsbnScannerModal, TitleAutocomplete, ExtractFromImageButton, EmailInvoiceDialog } from "@/pages/admin/InvoiceHelpers";

const SYMBOLS = { INR: "\u20b9", USD: "$", GBP: "\u00a3", EUR: "\u20ac" };
const CURRENCIES = ["INR", "USD", "GBP", "EUR"];

const EMPTY_ITEM = () => ({
  isbn: "",
  title: "",
  author: "",
  publisher: "",
  item_currency: "INR",
  qty: 1,
  price: 0,
  discount_percent: 0,
});

const EMPTY_REFS = () => ({ ref_no: "", po_number: "", po_date: "", delivery_challan_no: "", order_date: "" });
const EMPTY_COMPANY = () => ({
  name: "", address: "", phone: "", email: "", pan: "",
  bank_account_name: "", bank_account_number: "", bank_name: "", bank_branch: "", bank_ifsc: "",
  logo_url: "", signature_url: "",
});

const EMPTY_DOC = () => ({
  doc_number: "",
  customer: {
    name: "", library_name: "", email: "", phone: "",
    address: "", city: "", state: "", country: "India", pincode: "", gstin: "",
  },
  conversion_rates: { USD: 83.0, GBP: 105.0, EUR: 90.0 },
  items: [EMPTY_ITEM()],
  postage_freight: 0,
  extra_discount: 0,
  notes: "",
  show_bank_details: true,
  references: EMPTY_REFS(),
  company_override: EMPTY_COMPANY(),
});

function computePreview(doc) {
  const rates = doc.conversion_rates || {};
  let totalGross = 0;
  let totalFinal = 0;
  const rows = (doc.items || []).map((it) => {
    const cur = (it.item_currency || "INR").toUpperCase();
    const rate = cur === "INR" ? 1 : Number(rates[cur]) || 1;
    const qty = Number(it.qty) || 0;
    const price = Number(it.price) || 0;
    const disc = Number(it.discount_percent) || 0;
    const grossInr = +(price * qty * rate).toFixed(2);
    const finalInr = +(grossInr * (1 - disc / 100)).toFixed(2);
    totalGross += grossInr;
    totalFinal += finalInr;
    return { rate, grossInr, finalInr };
  });
  const postage = Number(doc.postage_freight) || 0;
  const extra = Number(doc.extra_discount) || 0;
  const netBefore = +(totalFinal + postage - extra).toFixed(2);
  const netAmount = Math.round(netBefore);
  const roundedOff = +(netAmount - netBefore).toFixed(2);
  return {
    rows,
    totalGross: +totalGross.toFixed(2),
    totalFinal: +totalFinal.toFixed(2),
    postage,
    extra,
    netBefore,
    netAmount,
    roundedOff,
  };
}

export default function LibraryInvoicePanel({
  kind = "LI",
  endpoint = "/admin/library-invoices",
  title = "Library Invoices",
  subtitle = "Landscape · Multi-currency conversion · Amount in words",
  requireCompanyOverride = false,
} = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const importInputRef = useRef(null);
  const [emailFor, setEmailFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint, { params: { limit: 100, q: q || undefined } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
  }, [endpoint, q]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ id: null, doc: EMPTY_DOC() });
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`${endpoint}/${id}`);
      setModal({
        id,
        doc: {
          doc_number: data.doc_number || "",
          customer: { ...EMPTY_DOC().customer, ...(data.customer || {}) },
          conversion_rates: data.conversion_rates || EMPTY_DOC().conversion_rates,
          items: data.items?.length ? data.items : [EMPTY_ITEM()],
          postage_freight: data.postage_freight || 0,
          extra_discount: data.extra_discount || 0,
          notes: data.notes || "",
          show_bank_details: data.show_bank_details ?? true,
          references: { ...EMPTY_REFS(), ...(data.references || {}) },
          company_override: { ...EMPTY_COMPANY(), ...(data.company_override || {}) },
        },
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id, doc_number) => {
    if (!window.confirm(`Delete ${doc_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success(`Deleted ${doc_number}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async (id, doc_number) => {
    try {
      const res = await api.get(`${endpoint}/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc_number.replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/admin/other-bills-template.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "other_bills_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const bulkImport = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/other-bills/bulk-import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} bill${data.created === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImportBusy(false); if (importInputRef.current) importInputRef.current.value = ""; }
  };

  const save = async () => {
    if (!modal) return;
    const d = modal.doc;
    if (!d.customer.name?.trim()) { toast.error("Customer / Library name is required"); return; }
    if (!d.items?.length || d.items.some((i) => !i.title?.trim())) { toast.error("Every item must have a title"); return; }
    if (requireCompanyOverride && !(d.company_override?.name || "").trim()) {
      toast.error("Company name is required for Other Company Bills");
      return;
    }
    setBusy(true);
    try {
      const cleanCustomer = { ...d.customer };
      if (!cleanCustomer.email?.trim()) cleanCustomer.email = null;
      const cleanRates = {};
      for (const c of ["USD", "GBP", "EUR"]) {
        const v = Number(d.conversion_rates?.[c]);
        if (v > 0) cleanRates[c] = v;
      }
      const payload = {
        customer: cleanCustomer,
        conversion_rates: cleanRates,
        items: d.items.map((i) => ({
          ...i,
          qty: Number(i.qty) || 0,
          price: Number(i.price) || 0,
          discount_percent: Number(i.discount_percent) || 0,
          item_currency: (i.item_currency || "INR").toUpperCase(),
        })),
        postage_freight: Number(d.postage_freight) || 0,
        extra_discount: Number(d.extra_discount) || 0,
        notes: d.notes || "",
        show_bank_details: !!d.show_bank_details,
        references: d.references || {},
        // If we require an override, always send whatever the admin typed
        // (backend enforces name presence). Otherwise strip fully blank.
        company_override: requireCompanyOverride
          ? (d.company_override || {})
          : ((d.company_override && Object.values(d.company_override).some((v) => (v || "").toString().trim())) ? d.company_override : null),
      };
      // Other-Bill custom doc-number is admin-editable; Library keeps auto.
      if (kind === "OB") {
        payload.doc_number = (d.doc_number || "").trim();
      }
      if (modal.id) await api.put(`${endpoint}/${modal.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(modal.id ? `${kind === "OB" ? "Other bill" : "Library invoice"} updated` : `${kind === "OB" ? "Other bill" : "Library invoice"} created`);
      setModal(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e7e5e4] bg-white" data-testid={`panel-${kind.toLowerCase()}`}>
      <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">{title}</div>
          <div className="text-lg font-black">{total} document{total === 1 ? "" : "s"}</div>
          <div className="text-[11px] text-[#71717a] mt-1">{subtitle}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={kind === "OB" ? "Search doc # or company" : "Search doc # or library"} className="border-2 border-[#e4e4e7] px-3 py-2 text-sm w-56" data-testid={`${kind}-search`} />
          {kind === "OB" && (
            <>
              <button onClick={downloadTemplate} className="border-2 border-[#09090b] hover:bg-[#09090b] hover:text-white font-bold px-3 py-2 text-xs flex items-center gap-1.5" data-testid={`${kind}-download-template`} title="Download CSV template">
                <Download className="w-3.5 h-3.5" /> Template
              </button>
              <input ref={importInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => bulkImport(e.target.files?.[0])} className="hidden" data-testid={`${kind}-import-file`} />
              <button onClick={() => importInputRef.current?.click()} disabled={importBusy} className="border-2 border-[#09090b] bg-[#fff7ed] hover:bg-[#09090b] hover:text-white font-bold px-3 py-2 text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid={`${kind}-import-btn`}>
                <Upload className="w-3.5 h-3.5" /> {importBusy ? "Importing…" : "Bulk Import CSV"}
              </button>
            </>
          )}
          <button onClick={openCreate} className="bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-4 py-2 text-sm flex items-center gap-1.5" data-testid={`${kind}-new`}>
            <Plus className="w-4 h-4" /> New {kind === "OB" ? "Other Bill" : "Library Invoice"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f4f4f5] uppercase text-[10px] tracking-wider">
            <tr>
              <th className="p-3 text-left">Doc #</th>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Library / Customer</th>
              <th className="p-3 text-right">Items</th>
              <th className="p-3 text-right">Net Amount ({SYMBOLS.INR})</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-border" data-testid={`LI-row-${d.doc_number.replace(/\//g,'_')}`}>
                <td className="p-3 font-mono font-bold">{d.doc_number}</td>
                <td className="p-3 text-xs text-[#71717a]">{d.created_at ? new Date(d.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                <td className="p-3">
                  <div className="font-bold">{d.customer?.name}</div>
                  {d.customer?.library_name && <div className="text-[11px] text-[#71717a]">{d.customer.library_name}</div>}
                </td>
                <td className="p-3 text-right">{(d.items || []).length}</td>
                <td className="p-3 text-right font-mono font-bold">{SYMBOLS.INR} {Number(d.net_amount || 0).toFixed(2)}</td>
                <td className="p-3 text-center whitespace-nowrap">
                  <button onClick={() => openEdit(d.id)} className="p-1.5 hover:bg-[#f4f4f5]" title="Edit" data-testid={`LI-edit-${d.doc_number.replace(/\//g,'_')}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => downloadPdf(d.id, d.doc_number)} className="p-1.5 hover:bg-[#f4f4f5]" title="Download PDF" data-testid={`LI-pdf-${d.doc_number.replace(/\//g,'_')}`}><Download className="w-4 h-4" /></button>
                  <button onClick={() => setEmailFor(d)} className="p-1.5 hover:bg-[#fff7ed] text-[#ea580c]" title="Email invoice" data-testid={`LI-email-${d.doc_number.replace(/\//g,'_')}`}><Mail className="w-4 h-4" /></button>
                  <button onClick={() => remove(d.id, d.doc_number)} className="p-1.5 hover:bg-[#fee2e2] text-[#dc2626]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#71717a] text-sm"><FileText className="w-8 h-8 mx-auto mb-2 text-[#a1a1aa]" />No library invoices yet — click <b>New Library Invoice</b> to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && <LibraryInvoiceModal modal={modal} setModal={setModal} save={save} busy={busy} kind={kind} requireCompanyOverride={requireCompanyOverride} />}
      <EmailInvoiceDialog
        open={!!emailFor}
        onClose={() => setEmailFor(null)}
        endpoint={emailFor ? (kind === "OB" ? `/admin/other-bills/${emailFor.id}/email` : `/admin/library-invoices/${emailFor.id}/email`) : ""}
        defaultTo={emailFor?.customer?.email || ""}
        docNumber={emailFor?.doc_number || ""}
      />
    </div>
  );
}

function LibraryInvoiceModal({ modal, setModal, save, busy, kind = "LI", requireCompanyOverride = false }) {
  const doc = modal.doc;
  const setDoc = (patch) => setModal((m) => ({ ...m, doc: { ...m.doc, ...patch } }));
  const setCust = (patch) => setDoc({ customer: { ...doc.customer, ...patch } });
  const setRate = (cur, val) => setDoc({ conversion_rates: { ...(doc.conversion_rates || {}), [cur]: val } });
  const setItem = (idx, patch) => {
    const items = doc.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setDoc({ items });
  };
  const addItem = () => setDoc({ items: [...doc.items, EMPTY_ITEM()] });
  const removeItem = (idx) => setDoc({ items: doc.items.filter((_, i) => i !== idx) });
  const preview = useMemo(() => computePreview(doc), [doc]);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Auto-fill from books DB. Called on ISBN blur or Title blur.
  const lookupIsbn = async (idx, rawIsbn) => {
    const clean = (rawIsbn || "").replace(/[^0-9Xx]/g, "");
    if (clean.length < 10) return;
    try {
      const { data } = await api.get(`/books/by-isbn/${clean}`);
      applyBookToItem(idx, data);
    } catch (e) { /* silent */ }
  };
  const applyBookToItem = (idx, book) => {
    if (!book) return;
    const cur = doc.items[idx] || {};
    const patch = {};
    if (!cur.title?.trim() && book.title) patch.title = book.title;
    if (!cur.author?.trim() && book.author) patch.author = book.author;
    if (!cur.publisher?.trim() && book.publisher) patch.publisher = book.publisher;
    if (!cur.isbn?.trim() && book.isbn) patch.isbn = book.isbn;
    const priceNow = Number(cur.price) || 0;
    if (priceNow === 0 && Number(book.mrp || book.price) > 0) patch.price = book.mrp || book.price;
    if (Object.keys(patch).length) {
      setItem(idx, patch);
      toast.success(`Auto-filled from catalog: ${book.title}`);
    }
  };
  const handleScanned = (isbn) => {
    // Find first empty row OR add a new row and set isbn + trigger lookup
    let idx = doc.items.findIndex((it) => !it.isbn?.trim() && !it.title?.trim());
    let items = [...doc.items];
    if (idx === -1) {
      items = [...items, { ...EMPTY_ITEM(), isbn }];
      idx = items.length - 1;
      setDoc({ items });
    } else {
      items[idx] = { ...items[idx], isbn };
      setDoc({ items });
    }
    // Give React a tick to render, then lookup
    setTimeout(() => lookupIsbn(idx, isbn), 100);
    setScannerOpen(false);
  };

  const handleExtracted = (extracted) => {
    // Map LLM output shape → Library item shape (price + discount_percent + item_currency).
    const mapped = extracted.map((it) => ({
      isbn: it.isbn || "",
      title: it.title || "",
      author: it.author || "",
      publisher: it.publisher || "",
      item_currency: (it.item_currency || "INR").toUpperCase(),
      qty: Number(it.qty) || 1,
      price: Number(it.price ?? it.mrp) || 0,
      discount_percent: Number(it.discount_percent) || 0,
    }));
    const isPlaceholder = doc.items.length === 1 && !doc.items[0].title?.trim() && !doc.items[0].isbn?.trim();
    setDoc({ items: isPlaceholder ? mapped : [...doc.items, ...mapped] });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 overflow-y-auto" onClick={() => !busy && setModal(null)} data-testid="LI-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-7xl border-2 border-[#09090b] my-6 max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b-2 border-[#09090b] flex items-center justify-between bg-[#fff7ed] sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c]">{modal.id ? "Edit" : "New"} Library Invoice</div>
            <div className="font-display text-xl font-black">Editable Preview · Landscape Format</div>
          </div>
          <button onClick={() => !busy && setModal(null)} className="p-1" data-testid="LI-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Editable Bill Number — only for Other Bills (Library uses auto GTP/…/LI) */}
          {kind === "OB" && (
            <div className="border-2 border-[#09090b] bg-[#fff7ed] p-3">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#09090b] mb-2">Bill Number</div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block flex-1 min-w-[240px]">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">Custom Bill No. {modal.id ? "" : "(leave blank to auto-generate)"}</div>
                  <input
                    value={doc.doc_number ?? ""}
                    onChange={(e) => setDoc({ doc_number: e.target.value })}
                    placeholder={modal.id ? doc.doc_number : "e.g. ALPHA/2026/047 — leave blank for GTP/…/OB"}
                    className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-1.5 text-sm outline-none font-mono"
                    data-testid={`${kind}-doc-number`}
                  />
                </label>
                <div className="text-[11px] text-[#71717a] max-w-md">
                  This company runs its own invoice series — set the exact number they use. Must be unique. Allowed: <b>A-Z 0-9 / - _ . #</b>
                </div>
              </div>
            </div>
          )}

          {/* Institutional refs */}
          <RefBlock refs={doc.references || {}} onChange={(refs) => setDoc({ references: refs })} kind={kind} />
          {/* Per-invoice company override — REQUIRED for Other Bills */}
          <CompanyOverrideBlock override={doc.company_override || {}} onChange={(co) => setDoc({ company_override: co })} kind={kind} forceOpen={requireCompanyOverride} required={requireCompanyOverride} />

          {/* Customer + Rates */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section title="Library / Bill To">
              <Field label="Library / Customer Name *" value={doc.customer.name} onChange={(v) => setCust({ name: v })} testid="LI-cust-name" />
              <Field label="Institution / Dept" value={doc.customer.library_name} onChange={(v) => setCust({ library_name: v })} />
              <Field label="Email" value={doc.customer.email} onChange={(v) => setCust({ email: v })} type="email" />
              <Field label="Phone" value={doc.customer.phone} onChange={(v) => setCust({ phone: v })} />
              <Field label="GSTIN (optional)" value={doc.customer.gstin} onChange={(v) => setCust({ gstin: v })} />
            </Section>
            <Section title="Address">
              <Field label="Address" value={doc.customer.address} onChange={(v) => setCust({ address: v })} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="City" value={doc.customer.city} onChange={(v) => setCust({ city: v })} />
                <Field label="State" value={doc.customer.state} onChange={(v) => setCust({ state: v })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Country" value={doc.customer.country} onChange={(v) => setCust({ country: v })} />
                <Field label="Pincode/ZIP" value={doc.customer.pincode} onChange={(v) => setCust({ pincode: v })} />
              </div>
            </Section>
            <Section title="Conversion Rates (per 1 unit → INR)">
              <div className="text-[11px] text-[#71717a] mb-2">Change any rate and totals below auto-recalculate. Rates apply to items priced in that currency.</div>
              {["USD", "GBP", "EUR"].map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <span className="w-14 font-bold">1 {c}</span>
                  <span>=</span>
                  <span>{SYMBOLS.INR}</span>
                  <input type="number" min="0" step="0.01" value={doc.conversion_rates?.[c] ?? ""} onChange={(e) => setRate(c, e.target.value)} className="border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-1.5 text-sm outline-none w-32" data-testid={`LI-rate-${c}`} />
                </label>
              ))}
              <label className="flex items-start gap-2 text-sm mt-3">
                <input type="checkbox" checked={doc.show_bank_details} onChange={(e) => setDoc({ show_bank_details: e.target.checked })} className="mt-1" data-testid="LI-bank-toggle" />
                <span>Show bank account details in footer</span>
              </label>
            </Section>
          </div>

          {/* Items */}
          <Section title={`Items (${doc.items.length})`}>
            <div className="flex flex-wrap gap-2 mb-2 items-center">
              <button type="button" onClick={() => setScannerOpen(true)} className="border-2 border-[#09090b] hover:bg-[#09090b] hover:text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid={`${kind}-scan-isbn`}>
                <Camera className="w-3.5 h-3.5" /> Scan ISBN
              </button>
              <ExtractFromImageButton kind={kind} onExtracted={handleExtracted} testid={`${kind}-extract-image`} />
              <TitleAutocomplete onPick={(book) => {
                // Add a new item pre-filled from picked book
                const items = [...doc.items];
                const emptyIdx = items.findIndex((it) => !it.isbn?.trim() && !it.title?.trim());
                if (emptyIdx >= 0) {
                  items[emptyIdx] = { ...items[emptyIdx], isbn: book.isbn || "", title: book.title || "", author: book.author || "", publisher: book.publisher || "", price: book.mrp || book.price || 0 };
                } else {
                  items.push({ ...EMPTY_ITEM(), isbn: book.isbn || "", title: book.title || "", author: book.author || "", publisher: book.publisher || "", price: book.mrp || book.price || 0 });
                }
                setDoc({ items });
                toast.success(`Added: ${book.title}`);
              }} />
              <div className="text-[11px] text-[#71717a] ml-auto">Upload bill photo · scan ISBN (camera or scanner) · or search catalog</div>
            </div>
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-xs" style={{ minWidth: '1200px' }}>
                <thead className="bg-[#09090b] text-white uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="p-2 w-8">#</th>
                    <th className="p-2 text-left w-24">ISBN</th>
                    <th className="p-2 text-left w-24">Author</th>
                    <th className="p-2 text-left" style={{ minWidth: '260px' }}>Title *</th>
                    <th className="p-2 text-left w-24">Publisher</th>
                    <th className="p-2 w-14">Cur.</th>
                    <th className="p-2 w-12">Qty</th>
                    <th className="p-2 w-16">Price</th>
                    <th className="p-2 w-20 text-right">Gross {SYMBOLS.INR}</th>
                    <th className="p-2 w-12">Disc %</th>
                    <th className="p-2 w-20 text-right">Final {SYMBOLS.INR}</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {doc.items.map((it, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`LI-item-${i}`}>
                      <td className="p-2 text-center text-[#71717a]">{i + 1}</td>
                      <td className="p-1"><input value={it.isbn} onChange={(e) => setItem(i, { isbn: e.target.value })} onBlur={(e) => lookupIsbn(i, e.target.value)} placeholder="ISBN" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs font-mono" data-testid={`LI-item-isbn-${i}`} /></td>
                      <td className="p-1"><input value={it.author} onChange={(e) => setItem(i, { author: e.target.value })} placeholder="Author" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" /></td>
                      <td className="p-1"><input value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} required placeholder="Book Title" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" data-testid={`LI-item-title-${i}`} /></td>
                      <td className="p-1"><input value={it.publisher} onChange={(e) => setItem(i, { publisher: e.target.value })} placeholder="Publisher" className="w-full px-2 py-1 border border-[#e4e4e7] text-xs" /></td>
                      <td className="p-1">
                        <select value={it.item_currency} onChange={(e) => setItem(i, { item_currency: e.target.value })} className="w-full px-1 py-1 border border-[#e4e4e7] text-xs" data-testid={`LI-item-cur-${i}`}>
                          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="p-1"><input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="w-full px-1 py-1 border border-[#e4e4e7] text-xs text-center" data-testid={`LI-item-qty-${i}`} /></td>
                      <td className="p-1"><input type="number" min="0" step="0.01" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} className="w-full px-1 py-1 border border-[#e4e4e7] text-xs text-right" data-testid={`LI-item-price-${i}`} /></td>
                      <td className="p-2 text-right font-mono">{preview.rows[i]?.grossInr?.toFixed(2)}</td>
                      <td className="p-1"><input type="number" min="0" max="100" step="0.1" value={it.discount_percent} onChange={(e) => setItem(i, { discount_percent: e.target.value })} className="w-full px-1 py-1 border border-[#e4e4e7] text-xs text-center" /></td>
                      <td className="p-2 text-right font-mono font-bold">{preview.rows[i]?.finalInr?.toFixed(2)}</td>
                      <td className="p-1 text-center">
                        {doc.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(i)} className="p-1 hover:bg-[#fee2e2] text-[#dc2626]"><X className="w-3.5 h-3.5" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addItem} className="mt-2 border-2 border-dashed border-[#e4e4e7] hover:border-[#ea580c] px-3 py-2 text-xs font-bold uppercase tracking-wider w-full" data-testid="LI-add-item">
              <Plus className="w-3 h-3 inline" /> Add another item
            </button>
          </Section>

          {/* Charges + Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Section title={`Charges & Extra Discount (all in ${SYMBOLS.INR})`}>
              <Field label={`Postage / Freight (${SYMBOLS.INR})`} value={doc.postage_freight} onChange={(v) => setDoc({ postage_freight: v })} type="number" testid="LI-postage" />
              <Field label={`Extra Discount (${SYMBOLS.INR})`} value={doc.extra_discount} onChange={(v) => setDoc({ extra_discount: v })} type="number" />
              <Field label="Terms & Conditions" value={doc.notes} onChange={(v) => setDoc({ notes: v })} multiline />
            </Section>
            <div className="border border-[#09090b] bg-[#f4f4f5] p-4 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-[#71717a]">Total Gross Amount</span><span className="font-mono">{SYMBOLS.INR} {preview.totalGross.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#71717a]">Less: Line Discount</span><span className="font-mono text-[#dc2626]">- {SYMBOLS.INR} {(preview.totalGross - preview.totalFinal).toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-[#d4d4d8] pt-1"><span className="text-[#71717a]">Sub-total after Discount</span><span className="font-mono">{SYMBOLS.INR} {preview.totalFinal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-[#71717a]">Add: Postage / Freight</span><span className="font-mono">{SYMBOLS.INR} {preview.postage.toFixed(2)}</span></div>
              {preview.extra > 0 && (
                <div className="flex justify-between"><span className="text-[#71717a]">Less: Extra Discount</span><span className="font-mono">- {SYMBOLS.INR} {preview.extra.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-[#71717a]">Rounded Off</span><span className="font-mono">{preview.roundedOff >= 0 ? "+" : ""}{SYMBOLS.INR} {preview.roundedOff.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t-2 border-[#09090b] text-lg font-black">
                <span>NET AMOUNT</span>
                <span className="font-mono text-[#ea580c]" data-testid="LI-net">{SYMBOLS.INR} {preview.netAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-[#09090b] flex items-center justify-end gap-2 bg-white sticky bottom-0">
          <button onClick={() => setModal(null)} disabled={busy} className="px-4 py-2 text-sm font-bold border-2 border-[#e4e4e7] hover:border-[#09090b] disabled:opacity-50" data-testid="LI-modal-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 text-sm font-bold bg-[#ea580c] hover:bg-[#c2410c] text-white disabled:opacity-60" data-testid="LI-modal-save">
            {busy ? "Saving…" : (modal.id ? "Save & Regenerate" : "Create Library Invoice")}
          </button>
        </div>
      </div>

      {scannerOpen && <IsbnScannerModal onClose={() => setScannerOpen(false)} onScanned={handleScanned} />}
    </div>
  );
}

// -------------------- Sub-components --------------------

function Section({ title, children }) {
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-2">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multiline = false, testid }) {
  const common = "w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-1.5 text-sm outline-none";
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a] mb-1">{label}</div>
      {multiline ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${common} min-h-[68px]`} data-testid={testid} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />
      )}
    </label>
  );
}
